import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Stack, TextField } from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Modal } from 'src/components/Molecules';
import { AuthorizationContext } from 'src/context/AuthorizationProvider';

const UsersSettings = () => {
  const { t } = useTranslation('admin');
  const { customerId } = useContext(AuthorizationContext);
  const [content, setContent] = useState(t('users.welcomeLetter'));
  const [testEmail, setTestEmail] = useState('');
  const [testModalOpen, setTestModalOpen] = useState(false);
  const isValidEmail = value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const invalidTestEmail = testEmail.trim() !== '' && !isValidEmail(testEmail.trim());

  useEffect(() => {
    const fetchWelcomeLetterEPU = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/property/${customerId}/welcome-letter-epu`);

        if (response.data.customer?.welcomeLetterEpu) {
          setContent(response.data.customer.welcomeLetterEpu);
        }
      } catch (error) {
        console.error(t('users.errorSave'), error);
        toast.error(t('users.errorSave'), {
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
          hideProgressBar: false,
          pauseOnHover: true,
          position: 'top-right'
        });
      }
    };

    fetchWelcomeLetterEPU();
  }, [customerId, t]);

  const handleEditorChange = newContent => {
    setContent(newContent);
  };

  const handleSave = async () => {
    try {
      const response = await axios.patch(`${process.env.REACT_APP_API_URL}/property/${customerId}/welcome-letter-epu`, {
        welcomeLetterEpu: content
      });

      toast.success(t('users.successSave'), {
        autoClose: 5000,
        closeOnClick: true,
        draggable: true,
        hideProgressBar: false,
        pauseOnHover: true,
        position: 'top-right'
      });
      console.log('Respuesta del servidor:', response.data);
    } catch (error) {
      toast.error(t('users.errorSave'), {
        autoClose: 5000,
        closeOnClick: true,
        draggable: true,
        hideProgressBar: false,
        pauseOnHover: true,
        position: 'top-right'
      });
      console.error(t('users.errorSave'), error);
    }
  };

  const handleSendTestEmail = async () => {
    try {
      const trimmed = testEmail.trim();
      if (trimmed && !isValidEmail(trimmed)) {
        toast.error('Ingresa un email válido o deja el campo vacío.', {
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
          hideProgressBar: false,
          pauseOnHover: true,
          position: 'top-right'
        });
        return;
      }
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/property/${customerId}/welcome-letter-epu/send`, {
        sentBy: 'c1be8d6f-8787-4707-aa94-15c911e5a65e',
        testEmail: trimmed || undefined
      });

      toast.success(t('users.successSendEmail'), {
        autoClose: 5000,
        closeOnClick: true,
        draggable: true,
        hideProgressBar: false,
        pauseOnHover: true,
        position: 'top-right'
      });
      console.log('Respuesta del servidor:', response.data);
      setTestModalOpen(false);
      setTestEmail('');
    } catch (error) {
      toast.error(t('users.errorSendEmail'), {
        autoClose: 5000,
        closeOnClick: true,
        draggable: true,
        hideProgressBar: false,
        pauseOnHover: true,
        position: 'top-right'
      });
      console.error(t('users.errorSendEmail'), error);
    }
  };

  return (
    <Box>
      <p>{t('users.welcomeMessage')}</p>

      <Editor
        init={{
          branding: false,
          content_css: 'default',
          height: 300,
          menubar: true,
          skin: 'oxide',
          toolbar: 'undo redo | formatselect | bold italic | alignleft aligncenter alignright | bullist numlist outdent indent | removeformat | help'
        }}
        onEditorChange={handleEditorChange}
        tinymceScriptSrc="/tinymce/tinymce.min.js"
        value={content}
      />

      <Box sx={{ alignItems: 'center', display: 'flex', gap: 2, mt: 2 }}>
        <Button color="primary" onClick={handleSave} variant="contained">
          {t('users.saveButton')}
        </Button>
        <Button color="primary" onClick={() => setTestModalOpen(true)} variant="text">
          {t('users.sendTestEmailButton')}
        </Button>
      </Box>

      {/* Modal para enviar prueba */}
      <Modal handleClose={() => setTestModalOpen(false)} labels={{ title: t('users.sendTestEmailButton') }} minWidth={450} open={testModalOpen}>
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            <TextField
              error={invalidTestEmail}
              fullWidth
              helperText={invalidTestEmail ? t('users.invalidEmail') : ''}
              label={t('users.testEmailLabel')}
              onChange={e => setTestEmail(e.target.value)}
              placeholder={t('users.emailPlaceholder')}
              size="small"
              type="email"
              value={testEmail}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button onClick={() => setTestModalOpen(false)} variant="outlined">
                {t('users.cancel')}
              </Button>
              <Button disabled={invalidTestEmail} onClick={handleSendTestEmail} variant="contained">
                {t('users.send')}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Modal>
    </Box>
  );
};

export default UsersSettings;
